import React, { useEffect, useMemo, useState } from 'react';
import { FiCheck, FiPrinter, FiX, FiLoader } from 'react-icons/fi';
import Modal from 'react-responsive-modal';

export interface ElectronPrintSelection {
  picking: boolean;
  labels: boolean;
  notices: boolean;
}

interface ElectronPrintSelectionModalProps {
  isOpen: boolean;
  initialSelection?: ElectronPrintSelection;
  onClose: () => void;
  onConfirm: (selection: ElectronPrintSelection) => void;
  isProcessing?: boolean;
}

const ResponsiveModal = Modal as unknown as React.ComponentType<any>;

const defaultSelection: ElectronPrintSelection = {
  picking: true,
  labels: true,
  notices: true,
};

const optionCopy: Record<keyof ElectronPrintSelection, { title: string; description: string }> = {
  picking: {
    title: 'Picking',
    description: 'Liste de picking pour chaque poste encore en cours.',
  },
  labels: {
    title: 'Étiquettes',
    description: 'Étiquettes colis et pages transporteur associées.',
  },
  notices: {
    title: 'Notices',
    description: 'Assembly instructions (A5 booklet on A4 sheets).',
  },
};

const ElectronPrintSelectionModal = ({
  isOpen,
  initialSelection,
  onClose,
  onConfirm,
  isProcessing = false,
}: ElectronPrintSelectionModalProps) => {
  const [selection, setSelection] = useState<ElectronPrintSelection>(
    initialSelection ?? defaultSelection,
  );

  useEffect(() => {
    if (isOpen) {
      setSelection(initialSelection ?? defaultSelection);
    }
  }, [isOpen, initialSelection]);

  const canConfirm = useMemo(
    () => Object.values(selection).some(Boolean) && !isProcessing,
    [selection, isProcessing],
  );

  const handleToggle = (key: keyof ElectronPrintSelection) => {
    if (isProcessing) return;
    setSelection(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(selection);
  };

  return (
    <ResponsiveModal
      open={isOpen}
      onClose={onClose}
      center
      classNames={{
        modal: 'rounded-xl p-0 overflow-hidden shadow-2xl bg-white max-w-xl w-full',
        overlay: 'backdrop-blur-sm bg-black/40',
      }}
      closeIcon={<FiX className="h-5 w-5" />}
      focusTrapped
    >
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-6">
        <div className="flex items-center gap-3">
          <div className="bg-white/10 rounded-full p-3">
            <FiPrinter className="h-6 w-6" />
          </div>
          <div>
            <p className="uppercase text-xs tracking-wide text-white/70">Impression Electron</p>
            <h2 className="text-2xl font-semibold">Sélectionner les documents à imprimer</h2>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5 bg-white">
        <p className="text-sm text-slate-600">
          Tous les éléments sont cochés par défaut. Décochez les documents que vous ne souhaitez pas envoyer à l’impression.
        </p>

        <div className="space-y-4">
          {(Object.keys(optionCopy) as Array<keyof ElectronPrintSelection>).map(key => {
            const option = optionCopy[key];
            const isChecked = selection[key];

            return (
              <button
                key={key}
                type="button"
                onClick={() => handleToggle(key)}
                className={`w-full border rounded-xl text-left transition-all px-4 py-3 flex items-start gap-3 ${
                  isChecked
                    ? 'border-slate-900 bg-slate-900 text-white shadow-lg'
                    : 'border-slate-200 hover:border-slate-400 bg-white'
                }`}
              >
                <span
                  className={`mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                    isChecked
                      ? 'border-white bg-white/20 text-white'
                      : 'border-slate-300 bg-white text-transparent'
                  }`}
                >
                  {isChecked && <FiCheck className="h-4 w-4" />}
                </span>
                <span>
                  <span className="block text-base font-semibold">{option.title}</span>
                  <span
                    className={`block text-sm leading-5 ${
                      isChecked ? 'text-white/80' : 'text-slate-600'
                    }`}
                  >
                    {option.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {!Object.values(selection).some(Boolean) && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            Sélectionnez au moins un document pour lancer l’impression.
          </div>
        )}
      </div>

      <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={isProcessing}
          className={`px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-white ${
            isProcessing ? 'cursor-not-allowed opacity-60' : ''
          }`}
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canConfirm}
          className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 ${
            canConfirm
              ? 'bg-slate-900 hover:bg-black'
              : 'bg-slate-400 cursor-not-allowed'
          }`}
        >
          {isProcessing ? (
            <FiLoader className="h-4 w-4 animate-spin" />
          ) : (
            <FiPrinter className="h-4 w-4" />
          )}
          {isProcessing ? 'Préparation…' : 'Confirmer'}
        </button>
      </div>
    </ResponsiveModal>
  );
};

export default ElectronPrintSelectionModal;
